import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { WithTooltip } from 'basic-components';
import { Exit } from 'basic-components/icons';
import { DataScienceEnums } from 'dashboard-services/api/dataScience';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { globalActions } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/modules/datastorage';
import dayjs from 'dayjs';

const Open = ({ valueType, value, output = false, provider = undefined }) => {
  const dispatch = useDispatch(),
        date = useSelector(state => state.datascienceState.creatorState.basicState.statuses?.[0]?.startTime),
        storageScreen = useSelector(state => state.myAccountState.settingsState.storageScreen),
        valueParsed = useMemo(() => typeof value === "string" && value.startsWith("{") ? CommonService.parseJson(value, value) : value, [value]),
        url = useMemo(() => {
          if(valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.GROUP || valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.GROUP_READ) {
            return DashboardLinkFactory.dataLake.group({ groupName: value, query: "", provider })
          } else if(valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATALAKE_FILE) {
            if(valueParsed?.id) {
              return DashboardLinkFactory.dataLake.file({ fileId: valueParsed.id, provider })
            } else {
              return DashboardLinkFactory.dataLake.group({ groupName: valueParsed?.groupName, query: `name=${valueParsed?.name}`, provider })
            }
          } else if(valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.SYMBOL) {
            return DashboardLinkFactory.dataStorage.newDataPrep({ groupName: valueParsed?.groupName, query: "", storageScreen, symbol: Object.entries(valueParsed?.symbols || {}).map(([k, v]) => `${k}=${v}`).join("|") })
          } else if(valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.PIPELINE) {
            return DashboardLinkFactory.dataScience.pipeline({ groupName: valueParsed.groupName, pipelineName: valueParsed.name })
          }
        }, [provider, storageScreen, value, valueParsed, valueType]),
        itemId = useMemo(() => valueParsed?.id ? valueParsed.id : (valueParsed?.name ? undefined : (typeof valueParsed === "string" ? valueParsed : undefined)), [valueParsed]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => new Query.Builder(getApiConfigLocal()).withShouldTimeOut(false).withHeader("Target", provider, true), [getApiConfigLocal, provider]),
        { data: dataprep, refetch: getDataPrep } = dataStorageQueries.useGetDataPrepById({ getQueryBuilder, id: itemId, enabled: false }),
        searchDpQuery = useMemo(() => {
          let query = `&name.keyword=${valueParsed?.name}`
          if(date && !output) {
            query += `&arrivalTime<${dayjs.utc(date).valueOf()}`
          } else if(date && output) {
            query += `&arrivalTime>${dayjs.utc(date).valueOf()}`
          }
          return query;
        }, [date, output, valueParsed]),
        { data: datapreps, refetch: searchDatapreps } = dataStorageQueries.useSearchDataPreps({ getQueryBuilder, query: searchDpQuery, shouldRefetch: false, latest: "all", size: 1, enabled: false }),
        openInNewTabLocal = useCallback(() => {
          if(url) {
            CommonService.openInNewTab(url)
            return;
          } 
          if(valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.DATA_PREP_PARAM) {
            const openDp = dataprep => {
              const { outputGroup, groupName, name, version } = dataprep || {}
              CommonService.openInNewTab(DashboardLinkFactory.dataStorage.dataPrep({ groupName: outputGroup || groupName, name, storageScreen, version }))
            }
            if(itemId) {
              if(dataprep) {
                return openDp(dataprep)
              } else {
                return getDataPrep().then(openDp)
              }
            } else {
              if(datapreps?.pages?.[0]?.items?.[0]) {
                return openDp(datapreps.pages[0].items[0])
              } else {
                return searchDatapreps()
                  .then(response => {
                    const items = response?.data?.pages?.[0]?.items
                    if(items?.[0]) {
                      openDp(items[0])
                    } else {
                      dispatch(globalActions.getMessageStore()).error("No dataprep found at the moment of task execution.")
                    }
                  })
              }
            }
          }
        }, [url, valueType, itemId, storageScreen, dataprep, getDataPrep, datapreps, searchDatapreps, dispatch])
          
  return (
    <WithTooltip
        data-place="top"
        short
        text="Open"
    >
      <div 
          className="data-science__pipeline-creator__body__options__resolver__header__controls__control"
          onClick={openInNewTabLocal}
      >
        <Exit
            color="blue-bright"
            height={16}
            width={16}
        />
      </div>
    </WithTooltip>
  )
}

Open.propTypes = {
  output: PropTypes.bool,
  provider: PropTypes.string,
  value: PropTypes.any.isRequired,
  valueType: PropTypes.string.isRequired,
}

export default Open;
